import React, { useState, useCallback, useRef } from 'react';
import { Printer } from 'lucide-react';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { getISOWeekNumber } from '../../../utils/calendar/dateHelpers';

type Orientation = 'landscape' | 'portrait';

interface PrintDialogProps {
  viewMode: string;
  currentYear: number;
  currentMonth: number;
  currentWeekStart: Date;
  onClose: () => void;
}

const DEFAULT_ORIENTATION: Record<string, Orientation> = {
  month: 'landscape',
  year: 'landscape',
  week: 'portrait',
};

export const PrintDialog: React.FC<PrintDialogProps> = ({ viewMode, currentYear, currentMonth, currentWeekStart, onClose }) => {
  const [printWithLegend, setPrintWithLegend] = useState(true);
  const [printWithNames, setPrintWithNames] = useState(true);
  const [orientation, setOrientation] = useState<Orientation>(DEFAULT_ORIENTATION[viewMode] || 'landscape');
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, true);

  const getPrintFilename = useCallback(() => {
    if (viewMode === 'year') return `Mozayc_${currentYear}`;
    if (viewMode === 'month') return `Mozayc_${currentYear}${String(currentMonth + 1).padStart(2, '0')}`;
    if (viewMode === 'week') return `Mozayc_${currentYear}WK${String(getISOWeekNumber(currentWeekStart)).padStart(2, '0')}`;
    return `Mozayc_${currentYear}`;
  }, [viewMode, currentYear, currentMonth, currentWeekStart]);

  const executePrint = useCallback(() => {
    const html = document.documentElement;
    if (printWithLegend) html.classList.add('print-show-legend');
    if (!printWithNames) html.classList.add('print-hide-names');
    html.classList.add(orientation === 'portrait' ? 'print-portrait' : 'print-landscape');
    // Disable pointer events so hover states don't appear in the print output
    html.classList.add('print-no-hover');

    const savedTitle = document.title;
    document.title = getPrintFilename();

    onClose();
    setTimeout(() => {
      const calendarFrame = document.querySelector('[data-calendar-frame]') as HTMLElement;
      if (calendarFrame) {
        const naturalW = calendarFrame.clientWidth;

        // For week view the grid scrolls — measure full scrollHeight of the grid container
        const weekGridContainer = document.querySelector('[data-week-grid-container]') as HTMLElement;
        const naturalH = weekGridContainer
          ? weekGridContainer.scrollHeight + (weekGridContainer.getBoundingClientRect().top - calendarFrame.getBoundingClientRect().top)
          : calendarFrame.clientHeight;

        // A4 at 96dpi: landscape 1122×794, portrait 794×1122
        // Browser scales other paper sizes proportionally from this baseline
        const pageW = orientation === 'landscape' ? 1122 : 794;
        const pageH = orientation === 'landscape' ? 794 : 1122;
        const marginPx = 2 * 0.5 * 37.8; // 0.5cm each side

        const contentW = pageW - marginPx;
        const contentH = pageH - marginPx;

        if (naturalW > 0 && naturalH > 0) {
          const scale = Math.min(contentW / naturalW, contentH / naturalH);
          html.style.setProperty('--print-scale', String(scale));
        }
      }

      window.print();
      document.title = savedTitle;
      html.style.removeProperty('--print-scale');
      html.classList.remove('print-show-legend', 'print-hide-names', 'print-portrait', 'print-landscape', 'print-no-hover');
    }, 150);
  }, [printWithLegend, printWithNames, orientation, onClose, getPrintFilename]);

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay)] backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fadeInBackdrop"
      onClick={onClose}
    >
      <div ref={dialogRef} role="dialog" aria-modal="true" className="bg-[var(--modal-bg)] rounded-xl w-full max-w-sm p-6 shadow-xl border border-[var(--border-primary)] animate-fadeIn"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-[var(--accent-subtle)] rounded-lg">
            <Printer className="w-5 h-5 text-[var(--accent-primary)]" />
          </div>
          <h3 className="text-lg font-semibold text-[var(--content-primary)]">Export</h3>
        </div>

        <div className="space-y-3 mb-6">
          {/* Orientation */}
          <div className="p-3 bg-[var(--surface-tertiary)] rounded-lg">
            <div className="text-sm font-medium text-[var(--content-primary)] mb-2">Orientation</div>
            <div className="flex gap-2">
              {(['landscape', 'portrait'] as const).map((o) => (
                <button
                  key={o}
                  onClick={() => setOrientation(o)}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    orientation === o
                      ? 'bg-[var(--accent-primary)] text-white'
                      : 'bg-[var(--surface-primary)] text-[var(--content-secondary)] hover:bg-[var(--sidebar-hover)]'
                  }`}
                >
                  {o.charAt(0).toUpperCase() + o.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Event names */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--surface-tertiary)] rounded-lg">
            <input
              type="checkbox"
              checked={printWithNames}
              onChange={(e) => setPrintWithNames(e.target.checked)}
              className="rounded border-[var(--input-border)] text-[var(--accent-primary)] focus:ring-[var(--accent-primary)]"
            />
            <div>
              <div className="text-sm font-medium text-[var(--content-primary)]">Show event names</div>
              <div className="text-xs text-[var(--content-secondary)]">Display event titles on the calendar</div>
            </div>
          </label>

          {/* Legend */}
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-[var(--surface-tertiary)] rounded-lg">
            <input
              type="checkbox"
              checked={printWithLegend}
              onChange={(e) => setPrintWithLegend(e.target.checked)}
              className="rounded border-[var(--input-border)] text-[var(--accent-primary)] focus:ring-[var(--accent-primary)]"
            />
            <div>
              <div className="text-sm font-medium text-[var(--content-primary)]">Include calendar legend</div>
              <div className="text-xs text-[var(--content-secondary)]">Show color-coded calendars below the calendar</div>
            </div>
          </label>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-[var(--surface-tertiary)] text-[var(--content-secondary)] rounded-lg hover:bg-[var(--sidebar-hover)] transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={executePrint}
            className="flex-1 px-4 py-2 bg-[var(--accent-primary)] text-[var(--content-inverted)] rounded-lg hover:bg-[var(--accent-hover)] transition-colors text-sm font-medium"
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};
